import { forwardRef } from 'react'

const VARIANT_CLASSES = {
  primary: 'bg-brand-red hover:bg-[#c1121f] active:bg-[#a00e18] text-white border-2 border-brand-red hover:border-[#c1121f]',
  secondary: 'bg-brand-charcoal hover:bg-[#1e2526] active:bg-[#111618] text-white border-2 border-brand-charcoal hover:border-[#1e2526]',
  ghost: 'bg-transparent hover:bg-white/10 active:bg-white/20 text-white border-2 border-white hover:border-white',
  'cta-phone': 'bg-brand-red hover:bg-[#c1121f] active:bg-[#a00e18] text-white border-2 border-brand-red hover:border-[#c1121f] btn-phone-pulse',
}

const SIZE_CLASSES = {
  sm: 'px-4 py-2 text-sm min-h-[36px]',
  md: 'px-6 py-3 text-base min-h-[44px]',
  lg: 'px-8 py-4 text-lg min-h-[52px]',
}

/**
 * Button component with multiple variants and sizes.
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'ghost'|'cta-phone'} props.variant
 * @param {'sm'|'md'|'lg'} props.size
 * @param {string} props.className - Additional classes
 * @param {boolean} props.disabled
 * @param {string} props.as - Render as different element (e.g. 'a')
 * @param {React.ReactNode} props.children
 */
const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    className = '',
    disabled = false,
    as: Component = 'button',
    children,
    ...props
  },
  ref
) {
  const baseClasses = [
    'inline-flex items-center justify-center gap-2',
    'font-heading font-bold tracking-wide uppercase',
    'rounded-md transition-all duration-200',
    'cursor-pointer select-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red',
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none',
  ].join(' ')

  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md

  const allClasses = [baseClasses, variantClass, sizeClass, className].join(' ')

  const buttonProps = {
    ref,
    className: allClasses,
    ...(Component === 'button' && { disabled, type: props.type || 'button' }),
    ...props,
  }

  if (variant === 'cta-phone') {
    return (
      <Component {...buttonProps} aria-label={props['aria-label'] || 'Call us now'}>
        {/* Phone Icon */}
        <svg
          aria-hidden="true"
          focusable="false"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.86 13a19.79 19.79 0 01-3.07-8.67A2 2 0 012.77 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
        </svg>
        {children}
      </Component>
    )
  }

  return <Component {...buttonProps}>{children}</Component>
})

export default Button
